import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import toastUtils from '../utils/toastUtils';
import { i18n } from '../translate/i18n';

export const useKanbanAnalytics = (ticketId = null) => {
  const [analytics, setAnalytics] = useState({
    summary: {
      totalTasks: 0,
      completedTasks: 0,
      inProgressTasks: 0,
      todoTasks: 0,
      blockedTasks: 0,
      overdueTasks: 0,
      completionRate: 0
    },
    priorityDistribution: {},
    timeTracking: {
      avgEstimatedHours: 0,
      avgActualHours: 0,
      totalEstimatedHours: 0,
      totalActualHours: 0
    },
    dailyStats: [],
    period: {
      startDate: null,
      endDate: null,
      days: 30
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Buscar analytics das tarefas
  const fetchTasksAnalytics = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);

    try {
      const queryParams = {
        dateRange: params.dateRange || '30',
        ...(ticketId && { ticketId }),
        ...(params.userId && { userId: params.userId })
      };

      const { data } = await api.get('/kanban-tasks/analytics', {
        params: queryParams
      });

      setAnalytics(data);
      return data;
    } catch (err) {
      console.error('Error fetching tasks analytics:', err);
      setError(err.message);
      toastUtils.error(i18n.t('kanban.messages.errorFetchingAnalytics'), 'Erro nos Dados');
      return null;
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  // Buscar progresso do ticket específico
  const fetchTicketProgress = useCallback(async (ticketId) => {
    setLoading(true);
    
    try {
      const { data } = await api.get(`/ticket/kanban/${ticketId}/progress`);
      return data;
    } catch (err) {
      console.error('Error fetching ticket progress:', err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Buscar analytics dashboard
  const fetchDashboardAnalytics = useCallback(async (params = {}) => {
    setLoading(true);
    
    try {
      const { data } = await api.get('/dashboard/kanban/stats', {
        params: {
          period: params.period || '30d',
          groupBy: params.groupBy || 'day',
          ...(params.userId && { userId: params.userId }),
          ...(params.priority && { priority: params.priority }),
          ...(params.status && { status: params.status })
        }
      });
      
      return data;
    } catch (err) {
      console.error('Error fetching dashboard analytics:', err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Calcular métricas de performance da equipe
  const calculateTeamPerformance = useCallback((tasks, users) => {
    const userStats = {};

    tasks.forEach(task => {
      if (task.assignee) {
        const userId = task.assignee.id;
        if (!userStats[userId]) {
          userStats[userId] = {
            user: task.assignee,
            totalTasks: 0,
            completedTasks: 0,
            inProgressTasks: 0,
            todoTasks: 0,
            blockedTasks: 0,
            totalEstimatedHours: 0,
            totalActualHours: 0,
            overdueTasks: 0
          };
        }

        const stats = userStats[userId];
        stats.totalTasks++;
        stats[`${task.status}Tasks`] = (stats[`${task.status}Tasks`] || 0) + 1;
        stats.totalEstimatedHours += task.estimatedHours || 0;
        stats.totalActualHours += task.actualHours || 0;

        if (task.dueDate && new Date(task.dueDate) < new Date() && task.status !== 'done') {
          stats.overdueTasks++;
        }
      }
    });

    return Object.values(userStats).map(stats => ({
      ...stats,
      completionRate: stats.totalTasks > 0 ? (stats.completedTasks / stats.totalTasks) * 100 : 0,
      avgTimePerTask: stats.completedTasks > 0 ? stats.totalActualHours / stats.completedTasks : 0,
      efficiency: stats.totalEstimatedHours > 0 
        ? Math.round(((stats.totalEstimatedHours - stats.totalActualHours) / stats.totalEstimatedHours) * 100)
        : 0
    }));
  }, []);

  // Calcular tendências
  const calculateTrends = useCallback((dailyStats) => {
    if (dailyStats.length < 2) return { completion: 0, creation: 0 };

    const recent = dailyStats.slice(-7);
    const previous = dailyStats.slice(-14, -7);

    const recentAvg = recent.reduce((sum, day) => sum + day.completed, 0) / recent.length;
    const previousAvg = previous.length > 0 
      ? previous.reduce((sum, day) => sum + day.completed, 0) / previous.length 
      : recentAvg;

    const recentCreation = recent.reduce((sum, day) => sum + day.created, 0) / recent.length;
    const previousCreation = previous.length > 0
      ? previous.reduce((sum, day) => sum + day.created, 0) / previous.length
      : recentCreation;

    return {
      completion: previousAvg > 0 ? ((recentAvg - previousAvg) / previousAvg) * 100 : 0,
      creation: previousCreation > 0 ? ((recentCreation - previousCreation) / previousCreation) * 100 : 0
    };
  }, []);

  // Gerar relatório de performance
  const generatePerformanceReport = useCallback(async (params = {}) => {
    setLoading(true);
    
    try {
      const analytics = await fetchTasksAnalytics(params);
      if (!analytics) return null;

      const report = {
        period: analytics.period,
        summary: analytics.summary,
        trends: calculateTrends(analytics.dailyStats),
        efficiency: {
          timeAccuracy: analytics.timeTracking.totalEstimatedHours > 0
            ? Math.round((1 - Math.abs(analytics.timeTracking.totalActualHours - analytics.timeTracking.totalEstimatedHours) / analytics.timeTracking.totalEstimatedHours) * 100)
            : 0,
          onTimeDelivery: analytics.summary.totalTasks > 0
            ? Math.round(((analytics.summary.totalTasks - analytics.summary.overdueTasks) / analytics.summary.totalTasks) * 100)
            : 0
        },
        recommendations: []
      };

      // Gerar recomendações baseadas nos dados
      if (report.efficiency.timeAccuracy < 70) {
        report.recommendations.push({
          type: 'time_estimation',
          message: i18n.t('kanban.analytics.recommendations.improveTimeEstimation'),
          priority: 'high'
        });
      }

      if (analytics.summary.overdueTasks > 0) {
        report.recommendations.push({
          type: 'overdue_tasks',
          message: i18n.t('kanban.analytics.recommendations.addressOverdueTasks'),
          priority: 'high',
          count: analytics.summary.overdueTasks
        });
      }

      if (report.trends.completion < -10) {
        report.recommendations.push({
          type: 'completion_trend',
          message: i18n.t('kanban.analytics.recommendations.improveThroughput'),
          priority: 'medium'
        });
      }

      return report;
    } catch (err) {
      console.error('Error generating performance report:', err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [fetchTasksAnalytics, calculateTrends]);

  return {
    analytics,
    loading,
    error,
    fetchTasksAnalytics,
    fetchTicketProgress,
    fetchDashboardAnalytics,
    calculateTeamPerformance,
    calculateTrends,
    generatePerformanceReport
  };
};

export default useKanbanAnalytics;